import React from "react";
import { useSelector } from "react-redux";
import { DocumentIcon } from "staysure-component-library";

import { IconButtonWrapper } from "@/components/atoms/button-wrapper/button-wrapper";
import TextSelectionButton from "@/components/atoms/text-selection-button/text-selection-button";
import withBrand from "@/components/with-brand";
import { BRAND_STAYSURE, PRODUCT } from "@/constants";
import { RenewalPolicyDocumentType } from "@/schema/renewal-page/review-page.schema";
import { RootState } from "@/store/root-reducer";

import DocumentItem from "./single-document-item/signle-document-item";

type CoverType =
  | "signature"
  | "basic"
  | "comprehensive"
  | "essentials"
  | "deluxe"
  | "classic"
  | "deluxe_cruise"
  | "classic_cruise";

type RelatedDocType = {
  linkUrl: string;
  linkText: string;
  linkId?: string;
  relevantCoverTypes: CoverType[];
};

function openInNewTab(url: string | undefined) {
  if (!url) return;
  window.open(url, "_blank", "noreferrer");
}

function RenewalDocument({
  documentSectionContent,
  brand = "staysure",
}: Readonly<{
  documentSectionContent: RenewalPolicyDocumentType | null;
  brand?: "staysure" | "avanti";
}>) {
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { tripDetails, mtaMedicalConsent } = attributes;

  const mapDocuments = (data: Record<string, RelatedDocType>) => {
    const findOption = (code: string) =>
      attributes.priceResult?.optionalExtras.find(
        (option) => option.code === code
      );

    const { selected: gadgetCoverSelected, state: gadgetCoverIncluded } =
      findOption("GADGET_COVER")?.price[0] ?? {};

    const { selected: carExcessSelected } =
      findOption("CAR_HIRE_EXCESS_WAIVER")?.price[0] ?? {};

    const relatedDocsArray: RelatedDocType[] = Object.values(data);

    const policy: CoverType =
      (attributes.tripDetails?.policyPackage?.toLocaleLowerCase() as unknown as CoverType) ??
      "";

    const dataArr = relatedDocsArray.map((item) => {
      if (
        !(
          (Array.isArray(item.relevantCoverTypes) &&
            item?.relevantCoverTypes
              .filter((type: string) => {
                return !(
                  item.relevantCoverTypes?.length === 1 &&
                  (type === "classic" || type === "deluxe")
                );
              })
              .includes(policy)) ||
          item.relevantCoverTypes.includes(
            tripDetails?.goingOnCruise &&
              (policy === "classic" || policy === "deluxe")
              ? `${policy}_cruise`
              : policy
          )
        )
      ) {
        return null;
      }

      if (
        item.linkId === "gadget-cover" &&
        attributes?.gadgetMigration?.isBeforeGadgetMigration === false &&
        PRODUCT === BRAND_STAYSURE
      ) {
        return null;
      }

      if (
        item.linkId === "gadget-cover" &&
        ((gadgetCoverIncluded !== "INCLUDED" &&
          gadgetCoverIncluded === "DISABLED") ||
          (gadgetCoverIncluded === "ENABLED" && !gadgetCoverSelected))
      ) {
        return null;
      }
      if (item.linkId === "car-excess" && !carExcessSelected) {
        return null;
      }
      return {
        link: item.linkUrl,
        label: item.linkText,
        id: item?.linkId,
      };
    });
    return dataArr;
  };

  const renderDocumentLinks = (documents: Record<string, RelatedDocType>) => {
    const documentData = mapDocuments(documents ?? {});
    return documentData
      .map(
        (
          doc: { link: string; label: string; id?: string } | null,
          index: number
        ) => {
          if (doc) {
            return (
              <div key={doc.label}>
                <DocumentItem
                  name={doc.label}
                  link={doc.link}
                  id={index}
                  brand={brand}
                />
              </div>
            );
          }
          return null;
        }
      )
      .filter(Boolean);
  };
  return (
    <div>
      <div className="mb-spacing-xl md:mb-spacing-xxl">
        <div className="flex items-center h-fit mt-spacing-m" key="full-doc">
          <IconButtonWrapper
            id="document-icon-default"
            hierarchy="tertiary"
            btnType="icon"
            isDisabled
            icon={<DocumentIcon stroke="rgba(39, 36, 39, 1)" />}
            className="!max-w-[24px] h-[24px] text-[24px] text-text-on-primary !max-h-[27px] !my-0 !px-0 !py-0 !bg-surface-neutral-option-1 active:!bg-surface-neutral-option-1 hover:!bg-surface-neutral-option-1"
          />
          <span className="text-sm md:text-xs font-semibold leading-[27px] pl-spacing-xs">
            <TextSelectionButton
              id="document-default"
              label={documentSectionContent?.fullPolicyDocumentTitle ?? ""}
              className={` text-[18px] font-body-font font-[600] align-top leading-[27px] !text-left h-fit
                   hover:text-interactive-secondary-default hover:bg-surface-neutral-option-1 !px-0`}
              onClick={() =>
                openInNewTab(
                  attributes.dynamicDocuments?.documentUrls.POLICY_WORDING
                )
              }
            />
          </span>
        </div>
        {renderDocumentLinks(documentSectionContent?.relatedDocs ?? {})}
      </div>

      {mtaMedicalConsent && mtaMedicalConsent.isTravellingBeforeRenewal && (
        <div className="mb-spacing-xl md:mb-spacing-xxl">
          <h2
            className="font-header-font md:text-[25px] text-[23px]
          leading-[27.6px] md:leading-[28px] text-text-title mb-spacing-l"
          >
            {documentSectionContent?.mtaSectionTitle}
          </h2>
          <div>
            <div
              className="flex items-center h-fit mt-spacing-m"
              key="full-doc"
            >
              <IconButtonWrapper
                id="document-icon-default"
                hierarchy="tertiary"
                btnType="icon"
                isDisabled
                icon={<DocumentIcon stroke="rgba(39, 36, 39, 1)" />}
                className="!max-w-[24px] h-[24px] text-[24px] text-text-on-primary !max-h-[27px] !my-0 !px-0 !py-0 !bg-surface-neutral-option-1 active:!bg-surface-neutral-option-1 hover:!bg-surface-neutral-option-1"
              />
              <span className="text-sm md:text-xs font-semibold leading-[27px] pl-spacing-xs">
                <TextSelectionButton
                  id="document-default"
                  label={
                    documentSectionContent?.mtafullPolicyDocumentTitle ?? ""
                  }
                  className={` text-[18px] font-body-font font-[600] align-top leading-[27px] !text-left h-fit
                   hover:text-interactive-secondary-default hover:bg-surface-neutral-option-1 !px-0`}
                  onClick={() =>
                    openInNewTab(
                      attributes.dynamicDocuments?.originalPolicyDocumentUrls
                        ?.renewalMtaPolicyWording
                    )
                  }
                />
              </span>
            </div>
            {renderDocumentLinks(documentSectionContent?.relatedMtaDocs ?? {})}
          </div>
        </div>
      )}
    </div>
  );
}

export default withBrand(RenewalDocument);
